import { useState } from 'react';

const Contact = () => {
  const [activeForm, setActiveForm] = useState<'contact' | 'exhibition'>('contact');
  const [contactFormData, setContactFormData] = useState({
    groupName: '',
    contactPerson: '',
    organization: '',
    position: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [exhibitionFormData, setExhibitionFormData] = useState({
    group_name: '',
    contact_person: '',
    phone: '',
    email: '',
    service_unit: '',
    job_title: '',
    visit_date: '',
    time_slot: '',
    attendees: 10,
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // 時段選項
  const timeSlots = [
    { value: 'AM_1030_1130', label: '上午 10:30–11:30', capacity: 20 },
    { value: 'PM_1400_1500', label: '下午 14:00–15:00', capacity: 20 }
  ];

  // 檢查日期是否為週一至週六
  const isValidDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDay();
    return day >= 1 && day <= 6; // 1-6 代表週一至週六
  };

  // 獲取可用時段（模擬已預約人數）
  const getAvailableSlots = (date: string) => {
    if (!date || !isValidDate(date)) return [];
    
    // 模擬已預約人數（實際應從後端獲取）
    const bookedCounts = {
      AM_1030_1130: Math.floor(Math.random() * 15),
      PM_1400_1500: Math.floor(Math.random() * 15)
    };

    return timeSlots.map(slot => ({
      ...slot,
      available: slot.capacity - bookedCounts[slot.value as keyof typeof bookedCounts],
      disabled: (slot.capacity - bookedCounts[slot.value as keyof typeof bookedCounts]) < exhibitionFormData.attendees
    }));
  };

  const handleContactInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExhibitionInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setExhibitionFormData(prev => ({
      ...prev,
      [name]: name === 'attendees' ? parseInt(value) || 10 : value
    }));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('type', 'contact');
      formDataToSend.append('groupName', contactFormData.groupName);
      formDataToSend.append('contactPerson', contactFormData.contactPerson);
      formDataToSend.append('organization', contactFormData.organization);
      formDataToSend.append('position', contactFormData.position);
      formDataToSend.append('email', contactFormData.email);
      formDataToSend.append('phone', contactFormData.phone);
      formDataToSend.append('subject', contactFormData.subject);
      formDataToSend.append('message', contactFormData.message);
      formDataToSend.append('notifyEmail', 'ckyen.cs@gmail.com');
      formDataToSend.append('status', '待處理');
      formDataToSend.append('contacted', '否');

      const response = await fetch('https://readdy.ai/api/form/submit/contact-form', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactFormData({
          groupName: '',
          contactPerson: '',
          organization: '',
          position: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setShowSuccessModal(true);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExhibitionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('group_name', exhibitionFormData.group_name);
      formDataToSend.append('contact_person', exhibitionFormData.contact_person);
      formDataToSend.append('phone', exhibitionFormData.phone);
      formDataToSend.append('email', exhibitionFormData.email);
      formDataToSend.append('service_unit', exhibitionFormData.service_unit);
      formDataToSend.append('job_title', exhibitionFormData.job_title);
      formDataToSend.append('visit_date', exhibitionFormData.visit_date);
      formDataToSend.append('time_slot', exhibitionFormData.time_slot);
      formDataToSend.append('attendees', exhibitionFormData.attendees.toString());
      formDataToSend.append('notes', exhibitionFormData.notes);
      formDataToSend.append('contacted', '否');
      formDataToSend.append('status', '待審核');

      const response = await fetch('https://readdy.ai/api/form/d487defjsic9gsa4uqtg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setExhibitionFormData({
          group_name: '',
          contact_person: '',
          phone: '',
          email: '',
          service_unit: '',
          job_title: '',
          visit_date: '',
          time_slot: '',
          attendees: 10,
          notes: ''
        });
        setShowSuccessModal(true);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableSlots = getAvailableSlots(exhibitionFormData.visit_date);

  return (
    <section className="py-20 bg-gradient-to-b from-amber-50 to-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 標題區域 */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Noto Serif TC", serif' }}>
            聯絡我們
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            歡迎與我們聯繫，了解更多協會活動資訊或參與文化傳承工作
          </p>
        </div>

        {/* 表單切換 */}
        <div className="flex justify-center mb-12">
          <div className="flex bg-white rounded-full p-1 shadow-lg">
            <button
              onClick={() => setActiveForm('contact')}
              className={`px-8 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                activeForm === 'contact'
                  ? 'bg-amber-600 text-white'
                  : 'text-gray-600 hover:text-amber-600'
              }`}
            >
              一般聯絡
            </button>
            <button
              onClick={() => setActiveForm('exhibition')}
              className={`px-8 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                activeForm === 'exhibition'
                  ? 'bg-amber-600 text-white'
                  : 'text-gray-600 hover:text-amber-600'
              }`}
            >
              參觀展覽（團體預約）
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* 聯絡資訊 */}
          <div className="space-y-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-stone-200">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '"Noto Sans TC", sans-serif' }}>
                協會資訊
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">地址</h4>
                    <p className="text-gray-600">台北市中正區重慶南路二段2.4號</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">電話</h4>
                    <p className="text-gray-600">(02) 2312-1272</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">電子郵件</h4>
                    <p className="text-gray-600">ckyen.cs@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">參觀時間</h4>
                    <p className="text-gray-600">週一至週五</p>
                    <p className="text-gray-600">上午 10:30-11:30</p>
                    <p className="text-gray-600">下午 14:00-15:00</p>
                    <p className="text-gray-600 text-sm text-amber-600">每場次上限30人</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 地圖 */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-stone-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3614.6985394!2d121.51089!3d25.03302!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3442a9a8b2b2b2b2%3A0x1234567890abcdef!2z5Lit6I-v6Ieq55Sx5LmL5a625ZyL5a6a5Y-k6LK55pWZ6IKy6Ieq55Sx5LmL5a62!5e0!3m2!1szh-TW!2stw!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="嚴家淦紀念園區 國定古蹟自由之家"
              ></iframe>
            </div>
          </div>

          {/* 表單區域 */}
          <div className="bg-white rounded-xl p-8 shadow-lg border border-stone-200">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '"Noto Sans TC", sans-serif' }}>
              {activeForm === 'contact' ? '聯絡表單' : '參觀展覽（團體預約）'}
            </h3>

            {/* 一般聯絡表單 */}
            {activeForm === 'contact' && (
              <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 mb-2">
                      團體名稱 *
                    </label>
                    <input
                      type="text"
                      id="groupName"
                      name="groupName"
                      value={contactFormData.groupName}
                      onChange={handleContactInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入團體名稱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                      聯絡人 *
                    </label>
                    <input
                      type="text"
                      id="contactPerson"
                      name="contactPerson"
                      value={contactFormData.contactPerson}
                      onChange={handleContactInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入聯絡人姓名"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                      服務單位 *
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={contactFormData.organization}
                      onChange={handleContactInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入服務單位"
                    />
                  </div>

                  <div>
                    <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                      職稱 *
                    </label>
                    <input
                      type="text"
                      id="position"
                      name="position"
                      value={contactFormData.position}
                      onChange={handleContactInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入職稱"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      電子郵件 *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={contactFormData.email}
                      onChange={handleContactInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入電子郵件"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      聯絡電話 *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={contactFormData.phone}
                      onChange={handleContactInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入聯絡電話"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    主旨 *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={contactFormData.subject}
                    onChange={handleContactInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm pr-8"
                  >
                    <option value="">請選擇主旨</option>
                    <option value="exhibition">2025紀念展詢問</option>
                    <option value="visit">參觀預約</option>
                    <option value="cooperation">合作提案</option>
                    <option value="donation">捐贈相關</option>
                    <option value="other">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    訊息內容 *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={contactFormData.message}
                    onChange={handleContactInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm resize-none"
                    placeholder="請詳細描述您的需求或問題..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">最多500字</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 disabled:from-gray-400 disabled:to-gray-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      送出中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-send-plane-line mr-2"></i>
                      送出訊息
                    </span>
                  )}
                </button>
              </form>
            )}

            {/* 展覽報名表單 */}
            {activeForm === 'exhibition' && (
              <form id="exhibition-form" data-readdy-form onSubmit={handleExhibitionSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="group_name" className="block text-sm font-medium text-gray-700 mb-2">
                      報名團體 *
                    </label>
                    <input
                      type="text"
                      id="group_name"
                      name="group_name"
                      value={exhibitionFormData.group_name}
                      onChange={handleExhibitionInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入團體名稱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-2">
                      聯絡人 *
                    </label>
                    <input
                      type="text"
                      id="contact_person"
                      name="contact_person"
                      value={exhibitionFormData.contact_person}
                      onChange={handleExhibitionInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入聯絡人姓名"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      聯絡電話 *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={exhibitionFormData.phone}
                      onChange={handleExhibitionInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入聯絡電話"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      電子信箱 *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={exhibitionFormData.email}
                      onChange={handleExhibitionInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入電子信箱"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="service_unit" className="block text-sm font-medium text-gray-700 mb-2">
                      服務單位
                    </label>
                    <input
                      type="text"
                      id="service_unit"
                      name="service_unit"
                      value={exhibitionFormData.service_unit}
                      onChange={handleExhibitionInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入服務單位"
                    />
                  </div>

                  <div>
                    <label htmlFor="job_title" className="block text-sm font-medium text-gray-700 mb-2">
                      職稱
                    </label>
                    <input
                      type="text"
                      id="job_title"
                      name="job_title"
                      value={exhibitionFormData.job_title}
                      onChange={handleExhibitionInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                      placeholder="請輸入職稱"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="visit_date" className="block text-sm font-medium text-gray-700 mb-2">
                      希望參觀日期 *
                    </label>
                    <input
                      type="date"
                      id="visit_date"
                      name="visit_date"
                      value={exhibitionFormData.visit_date}
                      onChange={handleExhibitionInputChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                    />
                    {exhibitionFormData.visit_date && !isValidDate(exhibitionFormData.visit_date) && (
                      <p className="text-xs text-red-500 mt-1">僅開放週一至週六參觀</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="attendees" className="block text-sm font-medium text-gray-700 mb-2">
                      參觀人數（最少 10 人成團） *
                    </label>
                    <input
                      type="number"
                      id="attendees"
                      name="attendees"
                      value={exhibitionFormData.attendees}
                      onChange={handleExhibitionInputChange}
                      required
                      min="10"
                      max="20"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm"
                    />
                  </div>
                </div>

                {exhibitionFormData.visit_date && isValidDate(exhibitionFormData.visit_date) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      參觀時段 *
                    </label>
                    <div className="space-y-3">
                      {availableSlots.map((slot) => (
                        <label
                          key={slot.value}
                          className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                            slot.disabled
                              ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
                              : exhibitionFormData.time_slot === slot.value
                              ? 'bg-amber-50 border-amber-500'
                              : 'bg-white border-gray-300 hover:border-amber-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="time_slot"
                              value={slot.value}
                              checked={exhibitionFormData.time_slot === slot.value}
                              onChange={handleExhibitionInputChange}
                              disabled={slot.disabled}
                              className="mr-3 text-amber-600 focus:ring-amber-500"
                            />
                            <span className={slot.disabled ? 'text-gray-400' : 'text-gray-900'}>
                              {slot.label}
                            </span>
                          </div>
                          <span className={`text-sm ${slot.disabled ? 'text-red-500' : 'text-green-600'}`}>
                            剩餘 {slot.available} 名額
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    特殊需求或備註
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={exhibitionFormData.notes}
                    onChange={handleExhibitionInputChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors duration-200 text-sm resize-none"
                    placeholder="如有特殊需求請說明（如無障礙設施、導覽語言等）"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">最多500字</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !exhibitionFormData.time_slot}
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 disabled:from-gray-400 disabled:to-gray-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      提交中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-calendar-check-line mr-2"></i>
                      提交團體預約
                    </span>
                  )}
                </button>
              </form>
            )}

            {/* 錯誤訊息 */}
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 mr-2"></i>
                  <p className="text-red-700">送出失敗，請稍後再試或直接聯絡我們。</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 成功提交彈窗 */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-green-600 text-2xl"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">敬請等候通知</h3>
            <p className="text-gray-600 mb-6">
              {activeForm === 'contact' 
                ? '您的聯絡訊息已成功送出，我們會盡快與您聯繫。' 
                : '已送出，將由工作人員審核後通知。'
              }
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap"
            >
              確定
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Contact;
